import { useLayoutEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import PortalHome from "./pages/PortalHome";
import ServiceEntry from "./pages/ServiceEntry";
import FoodHome from "./pages/FoodHome";
import StorePage from "./pages/StorePage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrdersPage from "./pages/OrdersPage";
import AccountPage from "./pages/AccountPage";
import { LocaleProvider } from "./context/Locale";
import AlertsPage from "./pages/AlertsPage";
import RootEntry from "./pages/RootEntry";
import { LegacyAddressRedirect, LegacyStoreRedirect } from "./pages/LegacyRouteRedirect";
import AddressesPage from "./pages/AddressesPage";
import WalletPage from "./pages/WalletPage";
import { AppStateProvider } from "./context/AppState";
import OrderDetailPage from "./pages/OrderDetailPage";
import RateOrderPage from "./pages/RateOrderPage";
import FavoritesPage from "./pages/FavoritesPage";
import ReviewsPage from "./pages/ReviewsPage";
import PagesIndex from "./pages/PagesIndex";
import DynamicPage from "./pages/DynamicPage";
import DriverLoginPage from "./pages/DriverLoginPage";
import DriverDashboard from "./pages/DriverDashboard";
import DriverOrdersPage from "./pages/DriverOrdersPage";
import DriverOrderDetailPage from "./pages/DriverOrderDetailPage";
import DriverCompletedPage from "./pages/DriverCompletedPage";
import DriverCommissionsPage from "./pages/DriverCommissionsPage";
import DriverProfilePage from "./pages/DriverProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ServiceOrderDetailPage from "./pages/ServiceOrderDetailPage";
import CategoryStoresPage from "./pages/CategoryStoresPage";
import ExplorePage from "./pages/ExplorePage";
import ServicesPage from "./pages/ServicesPage";
import YapePromosPage from "./pages/YapePromosPage";

function CustomerPageFrame({ children }) {
  return <div className="customer-page-frame">{children}</div>;
}

function customerPage(element) {
  return <CustomerPageFrame>{element}</CustomerPageFrame>;
}

const routeScrollPositions = new Map();

function RouteScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const locationKey = location.key || `${location.pathname}${location.search}`;
    const savedPosition = navigationType === "POP"
      ? Number(routeScrollPositions.get(locationKey) || 0)
      : 0;
    let cancelled = false;
    const timers = [];

    const restorePosition = () => {
      if (cancelled) return;
      window.scrollTo({ top: savedPosition, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = savedPosition;
      document.body.scrollTop = savedPosition;
    };

    restorePosition();
    if (navigationType === "POP" && savedPosition > 0) {
      [80, 240, 600].forEach((delay) => {
        timers.push(window.setTimeout(restorePosition, delay));
      });
    }

    return () => {
      cancelled = true;
      timers.forEach(window.clearTimeout);
      routeScrollPositions.set(locationKey, window.scrollY || document.documentElement.scrollTop || 0);
    };
  }, [location.key, location.pathname, location.search, navigationType]);

  return null;
}

export default function App() {
  return (
    <LocaleProvider>
      <AppStateProvider>
        <RouteScrollManager />
        <Routes>
        <Route path="/inicio" element={<PortalHome />} />
        <Route path="/services" element={customerPage(<ServicesPage />)} />
        <Route path="/food" element={customerPage(<FoodHome />)} />
        <Route path="/food/stores/:slug" element={customerPage(<StorePage />)} />
        <Route path="/services/:serviceId" element={customerPage(<ServiceEntry />)} />
        <Route path="/alerts" element={customerPage(<AlertsPage />)} />
        <Route path="/explore" element={customerPage(<ExplorePage />)} />
        <Route path="/promos-yape" element={customerPage(<YapePromosPage />)} />
        <Route path="/cart" element={customerPage(<CartPage />)} />
        <Route path="/checkout" element={customerPage(<CheckoutPage />)} />
        <Route path="/orders" element={customerPage(<OrdersPage />)} />
        <Route path="/orders/:uniqueOrderId" element={customerPage(<OrderDetailPage />)} />
        <Route path="/running-order/:uniqueOrderId" element={customerPage(<OrderDetailPage />)} />
        <Route path="/rate-order/:orderId" element={customerPage(<RateOrderPage />)} />
        <Route path="/favorites" element={customerPage(<FavoritesPage />)} />
        <Route path="/my-favorite-stores" element={<Navigate to="/favorites" replace />} />
        <Route path="/reviews/:slug" element={customerPage(<ReviewsPage />)} />
        <Route path="/pages" element={customerPage(<PagesIndex />)} />
        <Route path="/pages/:slug" element={customerPage(<DynamicPage />)} />
        <Route path="/delivery/login" element={<DriverLoginPage />} />
        <Route path="/delivery" element={<Navigate to="/delivery/orders" replace />} />
        <Route path="/delivery/summary" element={<DriverDashboard />} />
        <Route path="/delivery/orders" element={<DriverOrdersPage />} />
        <Route path="/delivery/orders/:reference" element={<DriverOrderDetailPage />} />
        <Route path="/delivery/completed-orders" element={<DriverCompletedPage />} />
        <Route path="/delivery/commissions" element={<DriverCommissionsPage />} />
        <Route path="/delivery/account" element={<DriverProfilePage />} />
        <Route path="/login/forgot-password" element={customerPage(<ForgotPasswordPage />)} />
        <Route path="/service-orders/:reference" element={customerPage(<ServiceOrderDetailPage />)} />
        <Route path="/account" element={customerPage(<AccountPage />)} />
        <Route path="/addresses" element={customerPage(<AddressesPage />)} />
        <Route path="/wallet" element={customerPage(<WalletPage />)} />
        <Route path="/stores/:restaurant" element={<LegacyStoreRedirect />} />
        <Route path="/stores/:restaurant/:id" element={<LegacyStoreRedirect />} />
        <Route path="/search-location" element={<LegacyAddressRedirect />} />
        <Route path="/my-location" element={<LegacyAddressRedirect />} />
        <Route path="/categories/stores" element={customerPage(<CategoryStoresPage />)} />
        <Route path="/login" element={<Navigate to="/account" replace />} />
        <Route path="/register" element={<Navigate to="/account?mode=register" replace />} />
        <Route path="/my-account" element={<Navigate to="/account" replace />} />
        <Route path="/my-addresses" element={<Navigate to="/addresses" replace />} />
        <Route path="/my-wallet" element={<Navigate to="/wallet" replace />} />
        <Route path="/my-orders" element={<Navigate to="/orders" replace />} />
        <Route path="/" element={<RootEntry />} />
        <Route path="*" element={<Navigate to="/inicio" replace />} />
        </Routes>
      </AppStateProvider>
    </LocaleProvider>
  );
}
